import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
	// undefined = checking, null = unauthenticated, object = signed in
	const [user, setUser] = useState(undefined);
	const [loading, setLoading] = useState(true);

	const refresh = useCallback(async () => {
		try {
			const { data } = await api.get('/auth/me');
			setUser(data);
			return data;
		} catch {
			setUser(null);
			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		// If returning from Emergent OAuth, AuthCallback will handle setting cookies
		// and updating the user. Avoid racing /auth/me with the callback exchange.
		if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
			setLoading(false);
			return;
		}
		refresh();
	}, [refresh]);

	const loginWithEmail = useCallback(async (email, password) => {
		const { data } = await api.post('/auth/login', { email, password });
		setUser(data.user);
		return data.user;
	}, []);

	const registerEmail = useCallback(async (name, email, password) => {
		const { data } = await api.post('/auth/register', { name, email, password });
		setUser(data.user);
		return data.user;
	}, []);

	const requestOtp = useCallback(async (mobile) => {
		const { data } = await api.post('/auth/otp/request', { mobile });
		return data;
	}, []);

	const verifyOtp = useCallback(async (mobile, code, name) => {
		const { data } = await api.post('/auth/otp/verify', { mobile, code, name });
		setUser(data.user);
		return data.user;
	}, []);

	const exchangeGoogleSession = useCallback(async (sessionId) => {
		const { data } = await api.post('/auth/google/session', { session_id: sessionId });
		setUser(data.user);
		return data.user;
	}, []);

	const logout = useCallback(async () => {
		try {
			await api.post('/auth/logout');
		} catch {
			/* ignore */
		}
		setUser(null);
	}, []);

	const value = {
		user,
		loading,
		refresh,
		loginWithEmail,
		registerEmail,
		requestOtp,
		verifyOtp,
		exchangeGoogleSession,
		logout,
		formatApiError,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used within AuthProvider');
	return ctx;
}
